import { Text, HStack, IconButton, Icon, Tooltip, chakra } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Pencil, Trash, GripVerticalIcon } from 'lucide-react';
import { useState, useCallback } from 'react';
import { Reorder, useDragControls } from 'framer-motion';

import { UnitAssessmentFormFieldType } from '@duke-hq/svc-api-db';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { trpcAdmin } from '../../../lib/trpcAdmin';
import { EditorMode } from '@duke-hq/shared-constants/assessments';

import { unitAssessmentState } from '../../UnitAssessmentState';
import type { FormFieldType } from './types';

const ListItem = chakra(Reorder.Item);

const fieldTypeDisplayMap = {
  [UnitAssessmentFormFieldType.TEXT]: () => t`Text Input`,
  [UnitAssessmentFormFieldType.RADIO]: () => t`Radio Buttons`,
  [UnitAssessmentFormFieldType.SELECT]: () => t`Dropdown Select`,
} as const;

type FormFieldProps = {
  field: FormFieldType;
  sectionId: string;
  setCurrentSlide: () => void;
  onDragEnd: () => void;
};

export function FormField({ field, sectionId, setCurrentSlide, onDragEnd }: FormFieldProps) {
  const [isDeleteFieldDialogOpen, setIsDeleteFieldDialogOpen] = useState(false);
  const utils = trpcAdmin.useUtils();
  const fieldTypeDisplay = fieldTypeDisplayMap[field.fieldType]();
  const itemDragControls = useDragControls();

  const deleteFormFieldMutation = trpcAdmin.unitAssessment.deleteFormField.useMutation({
    onSuccess: async () => {
      unitAssessmentState.setHasUnpublishedChanges(true);
      await utils.unitAssessment.getSlides.invalidate();
    },
  });

  const handleEditField = useCallback(() => {
    setCurrentSlide();
    unitAssessmentState.setCurrentFormFieldId(field.id);
    unitAssessmentState.setCurrentFormSectionId(sectionId);
    unitAssessmentState.setEditorType('FORM_FIELD');
    unitAssessmentState.setEditorMode(EditorMode.EDIT);
    unitAssessmentState.openEditor();
  }, [field.id, sectionId, setCurrentSlide]);

  const handleDeleteField = useCallback(() => {
    setIsDeleteFieldDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    deleteFormFieldMutation.mutate(field.id);
    setIsDeleteFieldDialogOpen(false);
    unitAssessmentState.closeEditor();
  }, [deleteFormFieldMutation, field.id]);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteFieldDialogOpen(false);
  }, []);

  return (
    <ListItem
      value={field}
      key={field.id}
      dragListener={false}
      position="relative"
      dragControls={itemDragControls}
      onDragEnd={onDragEnd}
      borderWidth="1px"
      borderRadius="md"
      p={3}
      bg="bg.subtle"
      mb={2}
    >
      <HStack justify="space-between">
        <HStack>
          <Text fontWeight="bold">{field.label}</Text>
          <Text>
            {fieldTypeDisplay}
            {field.required === true ? t`, required` : null}
          </Text>
        </HStack>
        <HStack spacing={1}>
          <Tooltip label={t`Edit field`} placement="top">
            <IconButton
              aria-label={t`Edit field`}
              icon={<Icon as={Pencil} boxSize="16px" />}
              size="sm"
              variant="ghost"
              onClick={handleEditField}
            />
          </Tooltip>
          <Tooltip label={t`Delete field`} placement="top">
            <IconButton
              aria-label={t`Delete field`}
              icon={<Icon as={Trash} boxSize="16px" />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={handleDeleteField}
            />
          </Tooltip>

          <Tooltip label={t`Drag to reorder`} placement="top">
            <IconButton
              icon={<Icon as={GripVerticalIcon} boxSize="16px" />}
              aria-label={t`Reorder option`}
              size="sm"
              variant="ghost"
              cursor="grab"
              onPointerDown={(e) => {
                itemDragControls.start(e);
              }}
            />
          </Tooltip>
        </HStack>
      </HStack>

      <ConfirmationDialog
        isOpen={isDeleteFieldDialogOpen}
        onClose={handleCancelDelete}
        title={<Trans>Delete Form Field</Trans>}
        body={
          <Trans>
            You are about to delete this form field. This action cannot be undone. Are you sure you
            want to continue?
          </Trans>
        }
        onConfirm={handleConfirmDelete}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
      />
    </ListItem>
  );
}
